import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { QuizLayout } from '../components/QuizLayout';
import { isValidEmail } from '../lib/validators';
import { useQuizStore } from '../store/quizStore';
import { useUser } from '../lib/useUser';

export function Login() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const expired = params.get('error') === 'expired';
  const leadEmail = useQuizStore((s) => s.leadEmail);
  const { status } = useUser();

  const [email, setEmail] = useState(leadEmail ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Already authenticated? Bounce to dashboard.
  useEffect(() => {
    if (status === 'authenticated') navigate('/dashboard', { replace: true });
  }, [status, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!isValidEmail(email)) {
      setError('Hmm, that email doesn’t look right.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/auth-request-magic', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body?.error ?? 'Could not send sign-in link. Please try again.');
        setSubmitting(false);
        return;
      }
      setSent(true);
    } catch {
      setError('Network error. Please try again.');
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <QuizLayout showBack={false}>
        <p className="eyebrow mb-3">Check your inbox</p>
        <h1 className="text-display-md text-ink mb-4">
          Sign-in link sent to <span className="text-brand-700">{email}</span>.
        </h1>
        <p className="text-ink-soft text-base mb-7">
          Click the button in your email to access your dashboard. The link expires
          in 15 minutes.
        </p>
        <p className="text-ink-soft text-sm">
          Wrong email?{' '}
          <button
            type="button"
            className="underline underline-offset-4 text-ink hover:text-brand-700"
            onClick={() => {
              setSent(false);
              setSubmitting(false);
              setError(null);
            }}
          >
            Try again
          </button>
        </p>
      </QuizLayout>
    );
  }

  return (
    <QuizLayout showBack={false}>
      <p className="eyebrow mb-3">Welcome back</p>
      <h1 className="text-display-md text-ink mb-4">Sign in to your dashboard.</h1>
      <p className="text-ink-soft text-base mb-7">
        {leadEmail
          ? `We'll send a one-click sign-in link to ${leadEmail}.`
          : 'Enter the email you used at checkout — we\'ll send you a one-click sign-in link.'}
      </p>

      {expired && (
        <div className="rounded-cta border-2 border-err bg-err/5 px-4 py-3 mb-6 text-sm text-err">
          That link has expired. Request a new one below.
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <label className="block text-sm font-semibold text-ink mb-1.5" htmlFor="email">
          Email
        </label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          inputMode="email"
          autoFocus
          className="w-full rounded-2xl border border-ink-light/40 bg-white px-5 py-4 text-base outline-none focus:border-brand focus:ring-2 focus:ring-brand-100 mb-4"
          required
        />

        {error && <p className="text-sm text-err mb-3">{error}</p>}

        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? 'Sending…' : 'Email me a sign-in link →'}
        </button>
      </form>
    </QuizLayout>
  );
}
